/**
 * Canonical FAQ content — the single source of truth for:
 *   - the /faq page (src/pages/FAQ.tsx)
 *   - the homepage FAQ section (src/components/landing/bass/FAQSectionBass.tsx)
 *   - the FAQPage JSON-LD emitted on both (via faqPageLd)
 *
 * Previously the /faq page and the homepage FAQ gave DIFFERENT answers for the
 * same questions (e.g. pricing), which is bad for AEO — answer engines quote
 * whichever they crawl and read the brand as inconsistent. Keep every answer
 * here consistent with PricingBass ($160 one-time, incl. 90 days of on-demand
 * styling). Change facts in ONE place: here.
 */
export interface Faq {
  question: string;
  answer: string;
  /** Optional inline CTA shown after the answer (presentational; not in schema). */
  link?: { label: string; href: string };
}

export const FAQS: Faq[] = [
  {
    question: "How does Cello digitize my closet? Do I need to take photos of everything?",
    answer:
      "No need to photograph every item! During our onboarding call, we'll show you how to capture your closet in under 30 minutes. Then our AI auto-tags, categorizes, and beautifully displays your items so you can admire them!",
  },
  {
    question: "How does Cello compare to traditional styling and Stitch Fix?",
    answer: "Click here to view the chart of how the three stack up.",
  },
  {
    question: "Who are the stylists behind Cello?",
    answer:
      "Our styling team is a collective of world-class wardrobe experts who have spent years at the heart of the fashion industry.\n\nWe pride ourselves in that.\n\nOur stylists aren't just trend-watchers and influencers. They're technically trained fashion experts from top institutions like Parsons, FIT, and the London College of Fashion. In fact, we only accept 1–2% of stylists who apply.\n\nTheir experience includes deep expertise within major fashion houses and on high-stakes editorial sets, where they've dressed everyone from celebrities for magazine covers to C-suite executives for the boardroom.\n\nMajority of our team is based in New York and across Europe. Our stylists bring that same high-level expertise usually reserved for film sets and runways, directly to your closet.\n\nYou'll get thoughtful, personalized guidance from pros who truly understand garment construction, silhouette, and how to make your existing wardrobe work harder for your specific goals and lifestyle.",
    link: { label: "Meet our stylists", href: "/stylist" },
  },
  {
    question: "How much should I expect to spend on clothes?",
    answer:
      "Unlike the traditional model, we don't take commission on your purchases. Our goal is to help you look good and feel confident. We work with spending budgets anywhere from $0 spend to $10k spend. We'll learn your spending comfort level and budget and help you maximize utility.",
  },
  {
    question: "What happens after I pay?",
    answer:
      "After checkout, you'll get access to our onboarding flow, where we guide you through a quick video walkthrough of your closet. Our team then digitizes your wardrobe using AI and retail-quality images. Once your closet's uploaded, your stylist builds a personalized lookbook with curated outfits and a style gap analysis.",
  },
  {
    question: "What if I'm not happy with it?",
    answer:
      "We're always still improving. Not happy? Let us know, and we'll make it right. We offer unlimited revisions on your style edit — until you're happy with it. We want every Cello user to walk away feeling more confident and in control of their wardrobe. Our only ask back is for feedback if it's not working for you.",
  },
];
